/**
 * Conversation Filter Helpers
 * ---------------------------
 * This file contains helper functions for filtering conversations in the frontend.
 * The filtering logic is designed to align with the backend SQL behavior to ensure
 * consistent results across the application.
 *
 * Key components:
 * 1. getValueFromConversation: Retrieves values from conversation objects, handling
 *    both top-level properties and nested attributes.
 * 2. matchesCondition: Evaluates a single filter condition against a value.
 * 3. matchesFilters: Evaluates a complete filter chain against a conversation.
 * 4. buildJsonLogicRule: Transforms evaluated filters into a JSON Logic frule that
 *    respects SQL-like operator precedence.
 *
 * Filter Structure:
 * -----------------
 * Each filter has the following structure:
 * {
 *   attributeKey: 'status',          // The attribute to filter on
 *   filterOperator: 'equal_to',      // The operator to use (equal_to, contains, etc.)
 *   values: ['open'],                // The values to compare against
 *   queryOperator: 'and'             // How this filter connects to the next one (and/or)
 * }
 *
 * Operator Precedence:
 * --------------------
 * The filter evaluation respects SQL-like operator precedence using JSON Logic:
 * https://www.postgresql.org/docs/17/sql-syntax-lexical.html#SQL-PRECEDENCE
 * 1. First evaluates individual conditions
 * 2. Then applies AND operators (groups consecutive AND conditions)
 * 3. Finally applies OR operators (connects AND groups with OR operations)
 *
 * This means that a filter chain like "A AND B OR C" is evaluated as "(A AND B) OR C",
 * and "A OR B AND C" is evaluated as "A OR (B AND C)".
 *
 * The implementation uses json-logic-js to apply these rules. The JsonLogic format is designed
 * to allow you to share rules (logic) between front-end and back-end code
 * Here we use json-logic-js to transform filter conditions into a nested JSON Logic structure that preserves proper
 * operator precedence, effectively mimicking SQL-like operator precedence.
 *
 * Conversation Object Structure:
 * -----------------------------
 * The conversation object can have:
 * 1. Top-level properties (status, priority, display_id, etc.)
 * 2. Nested properties in additional_attributes (browser_language, referer, etc.)
 * 3. Nested properties in custom_attributes (conversation_type, etc.)
 */
import jsonLogic from 'json-logic-js';
import {
  COMMA_SEPARATED_ATTRIBUTES,
  splitCommaSeparatedValues,
} from 'dashboard/helper/filterQueryGenerator';

/**
 * Determines the phone call type for a conversation based on phone attributes
 * This matches the backend logic in Conversations::FilterService
 * @param {Object} conversation - The conversation object
 * @returns {Array} - Array of phone call types that match this conversation
 */
const getPhoneCallType = conversation => {
  const phoneData = conversation.additional_attributes?.phone;
  if (!phoneData) {
    return [];
  }
  const callTypes = [];
  if (phoneData.direction === 'inbound') {
    callTypes.push('inbound');
  }
  if (phoneData.missed) {
    callTypes.push('missed');
  }
  if (phoneData.direction === 'outbound') {
    callTypes.push('outbound');
  }
  if (phoneData.unanswered_missed) {
    callTypes.push('unhandled_missed');
  }
  return callTypes;
};

const getAssigneeValueFromConversation = (conversation, options) => {
  let assigneeId = conversation.meta?.assignee?.id;
  if (assigneeId && assigneeId === options.currentUserId) {
    return ['current_user', assigneeId];
  }
  return assigneeId;
};

/**
 * Gets a value from a conversation based on the attribute key
 * @param {Object} conversation - The conversation object
 * @param {String} attributeKey - The attribute key to get the value for
 * @returns {*} - The value of the attribute
 *
 * This function handles various attribute locations:
 * 1. Direct properties on the conversation object (status, priority, etc.)
 * 2. Properties in conversation.additional_attributes (browser_language, referer, etc.)
 * 3. Properties in conversation.custom_attributes (conversation_type, etc.)
 */
const getValueFromConversation = (conversation, attributeKey, options) => {
  switch (attributeKey) {
    case 'status':
    case 'priority':
    case 'labels':
    case 'created_at':
    case 'last_activity_at':
    case 'last_customer_message_at':
      return conversation[attributeKey];
    // The conversation payload exposes display_id as `id`
    // (app/views/api/v1/models/_conversation.json.jbuilder), so reading
    // conversation.display_id here always came back undefined. It is a number,
    // while the filter input is text, so read it as text like the API does.
    case 'display_id': {
      const displayId = conversation.display_id ?? conversation.id;
      return displayId == null ? displayId : String(displayId);
    }
    case 'assignee_id':
      return getAssigneeValueFromConversation(conversation, options);
    case 'inbox_id':
      return conversation.inbox_id;
    case 'team_id':
      return conversation.meta?.team?.id;
    case 'browser_language':
    case 'country_code':
    case 'referer':
    case 'rating':
      return conversation.additional_attributes?.[attributeKey];
    case 'phone_call_type': {
      const phoneTypes = getPhoneCallType(conversation);
      return phoneTypes.length > 0 ? phoneTypes : null;
    }
    case 'csat':
      // CSAT (1-5) rating, serialized onto the conversation payload from the
      // csat_survey_responses table. null when the conversation has no survey.
      return conversation.csat ?? null;
    default:
      // Check if it's a custom attribute
      if (
        conversation.custom_attributes &&
        conversation.custom_attributes[attributeKey]
      ) {
        return conversation.custom_attributes[attributeKey];
      }
      return null;
  }
};

/**
 * Resolves the value from an input candidate
 * @param {*} candidate - The input value to resolve
 * @param {Object} options - Options for value resolution
 * @param {Number} options.currentUserId - The current user's ID for resolving "current_user"
 * @param {String} options.attributeKey - The attribute key being filtered for context
 * @returns {*} - If the candidate is an object with an id property, returns the id;
 *                If the candidate is "current_user" and attributeKey is "assignee_id", returns currentUserId;
 *                otherwise returns the candidate unchanged
 *
 * This helper function is used to normalize values, particularly when dealing with
 * objects that represent entities like users, teams, or inboxes where we want to
 * compare by ID rather than by the whole object.
 */
const resolveValue = (candidate, options = {}) => {
  // Handle "current_user" special case for assignee_id filters
  if (
    candidate === 'current_user' &&
    options.attributeKey === 'assignee_id' &&
    options.currentUserId
  ) {
    return options.currentUserId;
  }

  if (
    typeof candidate === 'object' &&
    candidate !== null &&
    'id' in candidate
  ) {
    return candidate.id;
  }

  return candidate;
};

/**
 * Checks if two values are equal in the context of filtering
 * @param {*} filterValue - The filterValue value
 * @param {*} conversationValue - The conversationValue value
 * @returns {Boolean} - Returns true if the values are considered equal according to filtering rules
 *
 * This function handles various equality scenarios:
 * 1. When both values are arrays: checks if any items in filterValue exist in conversationValue
 * 2. When filterValue is an array but conversationValue is not: checks if conversationValue is included in filterValue
 * 3. When conversationValue is an array but filterValue is not: checks if filterValue exists in conversationValue
 * 4. Otherwise: performs strict equality comparison
 */
const equalTo = (filterValue, conversationValue) => {
  if (Array.isArray(filterValue)) {
    if (filterValue.includes('all')) return true;
    if (filterValue === 'all') return true;

    if (Array.isArray(conversationValue)) {
      // For array values like labels or phone_call_type, check if any of the filter values exist in the conversation array
      return filterValue.some(val => conversationValue.includes(val));
    }

    if (!Array.isArray(conversationValue)) {
      return filterValue.includes(conversationValue);
    }
  }

  if (Array.isArray(conversationValue)) {
    // Handle case where conversation value is an array (like phone_call_type) but filter value is not
    return conversationValue.includes(filterValue);
  }

  return conversationValue === filterValue;
};

/**
 * Checks if the filterValue value is contained within the conversationValue value
 * @param {*} filterValue - The value to look for
 * @param {*} conversationValue - The value to search within
 * @returns {Boolean} - Returns true if filterValue is contained within conversationValue
 *
 * This function performs case-insensitive string containment checks.
 * For array conversation values (like rating), it checks whether any of the
 * filter values exist in the conversation array.
 */
const contains = (filterValue, conversationValue) => {
  if (Array.isArray(conversationValue)) {
    const candidates = Array.isArray(filterValue) ? filterValue : [filterValue];
    return candidates.some(val => conversationValue.includes(val));
  }
  if (typeof conversationValue === 'string') {
    if (Array.isArray(filterValue)) {
      return filterValue.some(
        val =>
          typeof val === 'string' &&
          conversationValue.toLowerCase().includes(val.toLowerCase())
      );
    }
    return conversationValue.toLowerCase().includes(filterValue.toLowerCase());
  }
  return false;
};

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Reduces a value to the UTC calendar day it falls on
 * @param {*} value - Epoch seconds, a `YYYY-MM-DD` string or another date string
 * @returns {Number|null} - Milliseconds at UTC midnight, or null when unparseable
 *
 * `new Date('YYYY-MM-DD')` is already UTC midnight, but local-time helpers such as
 * `setHours` move it to the browser's day, which differs from UTC in the evening.
 */
const toUtcDay = value => {
  let date;
  if (typeof value === 'number') {
    date = new Date(value * 1000);
  } else if (typeof value === 'string' && DATE_ONLY_PATTERN.test(value)) {
    date = new Date(`${value}T00:00:00.000Z`);
  } else {
    date = new Date(value);
  }
  if (Number.isNaN(date.getTime())) return null;

  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
};

/**
 * Compares a conversation date with a date filter value by whole UTC days
 * @param {*} conversationValue - The conversation date (epoch seconds or date string)
 * @param {*} filterValue - The filter date, a string or a one-item array in saved views
 * @param {Function} compareFn - Compares the two day values
 * @returns {Boolean} - Returns true if the comparison succeeds
 *
 * The backend casts both sides with `::date` (Filters::FilterHelper#date_filter), so
 * the time of day never takes part and the day is the one in UTC.
 */
const compareUtcDays = (conversationValue, filterValue, compareFn) => {
  const value = Array.isArray(filterValue) ? filterValue[0] : filterValue;
  const conversationDay = toUtcDay(conversationValue);
  const filterDay = toUtcDay(value);

  if (conversationDay === null || filterDay === null) return false;
  return compareFn(conversationDay, filterDay);
};

/**
 * Checks if a value matches a filter condition
 * @param {*} conversationValue - The value to check
 * @param {Object} filter - The filter condition
 * @param {Object} options - Options for value resolution
 * @param {Number} options.currentUserId - The current user's ID for resolving "current_user"
 * @returns {Boolean} - Returns true if the value matches the filter
 */
const matchesCondition = (conversationValue, filter, options = {}) => {
  const {
    filter_operator: filterOperator,
    values,
    attribute_key: attributeKey,
  } = filter;

  if (
    conversationValue === null ||
    conversationValue === undefined ||
    (Array.isArray(conversationValue) && conversationValue.length === 0)
  ) {
    return (
      filterOperator === 'is_not_present' || filterOperator === 'not_equal_to'
    );
  }

  const resolveOptions = { ...options, attributeKey };
  const resolved = Array.isArray(values)
    ? values.map(value => resolveValue(value, resolveOptions))
    : resolveValue(values, resolveOptions);

  // Typed as one string, compared against a list, exactly like the API payload.
  const filterValue = COMMA_SEPARATED_ATTRIBUTES.includes(attributeKey)
    ? splitCommaSeparatedValues(resolved)
    : resolved;

  // CSAT (1-5) uses numeric comparisons, whereas the comparison operators in the
  // main switch below assume date values (epoch seconds). Handle csat separately.
  if (attributeKey === 'csat') {
    const target = Number(
      Array.isArray(filterValue) ? filterValue[0] : filterValue
    );
    const current = Number(conversationValue);
    switch (filterOperator) {
      case 'equal_to':
        return equalTo(filterValue, conversationValue);
      case 'not_equal_to':
        return !equalTo(filterValue, conversationValue);
      case 'is_present':
        return true; // null already handled by the early return above
      case 'is_not_present':
        return false; // null already handled by the early return above
      case 'is_greater_than':
        return current > target;
      case 'is_less_than':
        return current < target;
      case 'is_greater_than_or_equal':
        return current >= target;
      case 'is_less_than_or_equal':
        return current <= target;
      default:
        return false;
    }
  }

  switch (filterOperator) {
    case 'equal_to':
      return equalTo(filterValue, conversationValue);

    case 'not_equal_to':
      return !equalTo(filterValue, conversationValue);

    case 'contains':
      return contains(filterValue, conversationValue);

    case 'does_not_contain':
      return !contains(filterValue, conversationValue);

    case 'is_present':
      return true; // We already handled null/undefined and empty arrays above

    case 'is_not_present':
      return false; // We already handled null/undefined and empty arrays above

    case 'is_greater_than':
      return compareUtcDays(conversationValue, filterValue, (a, b) => a > b);

    case 'is_less_than':
      return compareUtcDays(conversationValue, filterValue, (a, b) => a < b);

    case 'is_greater_than_or_equal':
      return compareUtcDays(conversationValue, filterValue, (a, b) => a >= b);

    case 'is_less_than_or_equal':
      return compareUtcDays(conversationValue, filterValue, (a, b) => a <= b);

    case 'newer_than_days': {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const daysInMilliseconds = filterValue * 24 * 60 * 60 * 1000;
      const targetDate = new Date(today.getTime() - daysInMilliseconds);

      return conversationValue * 1000 >= targetDate.getTime();
    }

    case 'older_than_days': {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const daysInMilliseconds = filterValue * 24 * 60 * 60 * 1000;
      const targetDate = new Date(today.getTime() + daysInMilliseconds);

      return conversationValue * 1000 <= targetDate.getTime();
    }

    default:
      return false;
  }
};

/**
 * Converts an array of evaluated filters into a JSON Logic rule
 * that respects SQL-like operator precedence (AND before OR)
 *
 * This function transforms the linear sequence of filter results and operators
 * into a nested JSON Logic structure that correctly implements SQL-like precedence:
 * - AND operators are evaluated before OR operators
 * - Consecutive AND conditions are grouped together
 * - These AND groups are then connected with OR operators
 *
 * For example:
 * - "A AND B AND C" becomes { "and": [A, B, C] }
 * - "A OR B OR C" becomes { "or": [A, B, C] }
 * - "A AND B OR C" becomes { "or": [{ "and": [A, B] }, C] }
 * - "A OR B AND C" becomes { "or": [A, { "and": [B, C] }] }
 *
 * FILTER CHAIN:  A --AND--> B --OR--> C --AND--> D --AND--> E --OR--> F
 *                |         |         |         |         |         |
 *                v         v         v         v         v         v
 * EVALUATED:    true      false     true      false     true      false
 *                \         /         \         \         /         /
 *                 \       /           \         \       /         /
 *                  \     /             \         \     /         /
 *                   \   /               \         \   /         /
 *                    \ /                 \         \ /         /
 * AND GROUPS:      [true,false]          [true,false,true]    [false]
 *                     |                       |                  |
 *                     v                       v                  v
 * JSON LOGIC:    {"and":[true,false]}   {"and":[true,false,true]}  false
 *                   \                      |                  /
 *                    \                     |                 /
 *                     \                    |                /
 *                      \                   |               /
 *                       \                  |              /
 * FINAL RULE:            {"or":[{"and":[true,false]},{"and":[true,false,true]},false]}
 *
 * {
 *  "or": [
 *    { "and": [true, false] },
 *    { "and": [true, false, true] },
 *    { "and": [false] }
 *  ]
 * }
 * @param {Array} evaluatedFilters - Array of evaluated filter conditions with results and operators
 * @returns {Object} - JSON Logic rule
 */
const buildJsonLogicRule = evaluatedFilters => {
  // Step 1: Group consecutive AND conditions into logical units
  // This implements the higher precedence of AND over OR
  const andGroups = [];
  let currentAndGroup = [evaluatedFilters[0].result];

  for (let i = 0; i < evaluatedFilters.length - 1; i += 1) {
    if (evaluatedFilters[i].operator === 'and') {
      // When we see an AND operator, we add the next filter to the current AND group
      // This builds up chains of AND conditions that will be evaluated together
      currentAndGroup.push(evaluatedFilters[i + 1].result);
    } else {
      // When we see an OR operator, it marks the boundary between AND groups
      // We finalize the current AND group and start a new one

      // If the AND group has only one item, don't wrap it in an "and" operator
      // Otherwise, create a proper "and" JSON Logic expression
      andGroups.push(
        currentAndGroup.length === 1
          ? currentAndGroup[0] // Single item doesn't need an "and" wrapper
          : { and: currentAndGroup } // Multiple items need to be AND-ed together
      );

      // Start a new AND group with the next filter's result
      currentAndGroup = [evaluatedFilters[i + 1].result];
    }
  }

  // Step 2: Add the final AND group that wasn't followed by an OR
  if (currentAndGroup.length > 0) {
    andGroups.push(
      currentAndGroup.length === 1
        ? currentAndGroup[0] // Single item doesn't need an "and" wrapper
        : { and: currentAndGroup } // Multiple items need to be AND-ed together
    );
  }

  // Step 3: Combine all AND groups with OR operators
  // If we have multiple AND groups, they are separated by OR operators
  // in the original filter chain, so we combine them with an "or" operation
  if (andGroups.length > 1) {
    return { or: andGroups };
  }

  // If there's only one AND group (which might be a single condition
  // or multiple AND-ed conditions), just return it directly
  return andGroups[0];
};

/**
 * Evaluates each filter against the conversation and prepares the results array
 * @param {Object} conversation - The conversation to evaluate
 * @param {Array} filters - Filters to apply
 * @param {Object} options - Options for value resolution
 * @param {Number} options.currentUserId - The current user's ID for resolving "current_user"
 * @returns {Array} - Array of evaluated filter results with operators
 */
const evaluateFilters = (conversation, filters, options = {}) => {
  return filters.map((filter, index) => {
    const value = getValueFromConversation(
      conversation,
      filter.attribute_key,
      options
    );
    const result = matchesCondition(value, filter, options);

    // This part determines the logical operator that connects this filter to the next one:
    // - If this is not the last filter (index < filters.length - 1), use the filter's query_operator
    //   or default to 'and' if query_operator is not specified
    // - If this is the last filter, set operator to null since there's no next filter to connect to
    const isLastFilter = index === filters.length - 1;
    const operator = isLastFilter ? null : filter.query_operator || 'and';

    return { result, operator };
  });
};

/**
 * Checks if a conversation matches the given filters
 * @param {Object} conversation - The conversation object to check
 * @param {Array} filters - Array of filter conditions
 * @param {Object} options - Options for value resolution
 * @param {Number} options.currentUserId - The current user's ID for resolving "current_user"
 * @returns {Boolean} - Returns true if conversation matches filters, false otherwise
 */
export const matchesFilters = (conversation, filters, options = {}) => {
  // If no filters, return true
  if (!filters || filters.length === 0) {
    return true;
  }

  // Handle single filter case
  if (filters.length === 1) {
    const value = getValueFromConversation(
      conversation,
      filters[0].attribute_key,
      options
    );
    return matchesCondition(value, filters[0], options);
  }

  // Evaluate all conditions and prepare for jsonLogic
  const evaluatedFilters = evaluateFilters(conversation, filters, options);
  return jsonLogic.apply(buildJsonLogicRule(evaluatedFilters));
};
